import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Database,
  TrendingUp,
  Sparkles,
  RefreshCw,
  Clock,
  LayoutGrid,
  List,
  Package,
  ChevronLeft,
  ChevronRight,
  Hash,
  Loader,
  AlertCircle,
  X,
  Star,
  Activity,
  Search,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useDebounce } from '../hooks/useDebounce';
import { useIsMobile } from '../hooks/useIsMobile';
import { cn } from '../utils/cn';
import { apogeeApiService } from '../services/apogeeApi';
import {
  PackRow,
  RecentUpdateRow,
  DownloadGrowthRow,
  EmergingRow,
  PACK_BROWSER_CATALOG_SORT_PRESETS,
  ApogeeSearchDisabledError,
  SEARCH_PACKS_MAX_LIMIT,
  APOGEE_ACTIVITY_WINDOWS,
  ApogeeActivityWindow,
  ModlistHashLookupResponse,
  CFModlistHashLookupResponse,
} from '../types/apogee';
import {
  enrichmentAuthorDisplayLine,
  enrichmentFromPackRow,
  enrichmentSearchBlob,
} from '../utils/apogeeEnrichment';
import { apogeeIdFromSearchHit, packRowFromSearchHit } from '../utils/packSearch';
import { packDetailPath } from '../utils/packRoutes';
import { PackCard } from '../components/apogee/PackCard';
import { ApogeePackCatalogSearchBar } from '../components/apogee/ApogeePackCatalogSearchBar';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeader } from '../components/ui/SectionHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { StatTile } from '../components/ui/StatTile';

// ── Types ───────────────────────────────────────────────────────────────────

type ViewMode = 'grid' | 'table';
type BrowserTab = 'all' | 'recent' | 'growth' | 'emerging';

const PAGE_SIZES_BROWSE = [25, 50, 100, 200] as const;
const PAGE_SIZES_SEARCH = [25, 50, 100] as const;

const RECENT_SORT_PRESETS = [
  { value: '-last_updated', label: 'Recently updated' },
  { value: '-observed_at', label: 'Last observed' },
] as const;

/** Client-only sort token: API returns top rows by absolute delta; we re-rank by relative growth. */
const GROWTH_SORT_RELATIVE_CLIENT = 'client:-relative_growth' as const;

const GROWTH_SORT_PRESETS = [
  { value: '-delta_downloads', label: 'Most growth (absolute)' },
  { value: GROWTH_SORT_RELATIVE_CLIENT, label: 'Most growth (relative)' },
  { value: '-download_count_latest', label: 'Most downloads' },
  { value: 'name', label: 'Name A–Z' },
] as const;

const EMERGING_SORT_PRESETS = [
  { value: '-delta_downloads', label: 'Most growth' },
  { value: '-first_seen_at', label: 'Newest first seen' },
  { value: 'name', label: 'Name A–Z' },
] as const;

type FeaturedFilter = 'any' | 'featured' | 'not_featured';

const ACTIVITY_CATALOG_SORT_PRESETS: { value: string; label: string }[] = [
  { value: '-activity_score_30d', label: 'Activity score (30d)' },
  { value: 'activity_rank_30d', label: 'Activity rank (30d, best first)' },
  { value: '-activity_score_7d', label: 'Activity score (7d)' },
  { value: '-activity_score_90d', label: 'Activity score (90d)' },
];

const SINCE_HOURS_OPTIONS = [
  { value: 24, label: '24 hours' },
  { value: 48, label: '48 hours' },
  { value: 168, label: '7 days' },
  { value: 720, label: '30 days' },
];

const WINDOW_DAYS_OPTIONS = [
  { value: 1, label: '1 day' },
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
];

const formatNumber = (n: number | undefined | null): string => {
  if (n == null || Number.isNaN(n)) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
};

const parseSortToken = (sort: string): { field: string; desc: boolean } => {
  const desc = sort.startsWith('-');
  return { field: desc ? sort.slice(1) : sort, desc };
};

const growthFactorVsBaseline = (row: DownloadGrowthRow): number | null => {
  const b = row.download_count_baseline;
  if (b <= 0) return null;
  return row.download_count_latest / b;
};

const formatRelativeGrowthFactor = (factor: number | null): string => {
  if (factor == null || !Number.isFinite(factor)) return '—';
  return `${factor.toFixed(factor < 10 ? 2 : 1)}×`;
};

const filterPacksClient = (packs: PackRow[], query: string): PackRow[] => {
  const q = query.trim().toLowerCase();
  if (!q) return packs;
  return packs.filter((p) => {
    const blob = enrichmentSearchBlob(enrichmentFromPackRow(p));
    return (
      p.name.toLowerCase().includes(q) ||
      p.slug.toLowerCase().includes(q) ||
      p.summary.toLowerCase().includes(q) ||
      (p.mpi_id != null && String(p.mpi_id).includes(q)) ||
      (p.cf_mod_id != null && String(p.cf_mod_id).includes(q)) ||
      String(p.apogee_id).includes(q) ||
      blob.includes(q)
    );
  });
};

const applyClientSort = (rows: PackRow[], sortToken: string): PackRow[] => {
  const { field, desc } = parseSortToken(sortToken);
  return [...rows].sort((a, b) => {
    let cmp = 0;
    switch (field) {
      case 'download_count':
        cmp = (a.download_count ?? 0) - (b.download_count ?? 0);
        break;
      case 'popularity_rank': {
        const ar = a.popularity_rank ?? Number.POSITIVE_INFINITY;
        const br = b.popularity_rank ?? Number.POSITIVE_INFINITY;
        cmp = ar - br;
        break;
      }
      case 'last_updated':
      case 'first_seen_at':
      case 'first_release_at': {
        const at = new Date((a[field] as string | null | undefined) || 0).getTime();
        const bt = new Date((b[field] as string | null | undefined) || 0).getTime();
        cmp = at - bt;
        break;
      }
      case 'name':
        cmp = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        break;
      default: {
        if (field.startsWith('activity_score_')) {
          const w = field.slice('activity_score_'.length) as ApogeeActivityWindow;
          cmp =
            (a.apogee_activity?.windows?.[w]?.score ?? Number.NEGATIVE_INFINITY) -
            (b.apogee_activity?.windows?.[w]?.score ?? Number.NEGATIVE_INFINITY);
          break;
        }
        if (field.startsWith('activity_rank_')) {
          const w = field.slice('activity_rank_'.length) as ApogeeActivityWindow;
          const ar = a.apogee_activity?.windows?.[w]?.rank ?? Number.POSITIVE_INFINITY;
          const br = b.apogee_activity?.windows?.[w]?.rank ?? Number.POSITIVE_INFINITY;
          cmp = ar - br;
          break;
        }
        return 0;
      }
    }
    return desc ? -cmp : cmp;
  });
};

// ── Hash Lookup Panel ───────────────────────────────────────────────────────

const HashLookupPanel = ({ onClose }: { onClose: () => void }) => {
  const navigate = useNavigate();
  const [hashType, setHashType] = useState<'mpi' | 'cf'>('mpi');
  const [hashInput, setHashInput] = useState('');
  const [hashSort, setHashSort] = useState('-download_count');
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mpiResults, setMpiResults] = useState<ModlistHashLookupResponse | null>(null);
  const [cfResults, setCfResults] = useState<CFModlistHashLookupResponse | null>(null);

  const handleSearch = async () => {
    const trimmed = hashInput.trim();
    if (!/^[a-fA-F0-9]{64}$/.test(trimmed)) {
      setError('Hash must be a 64-character hexadecimal string (SHA-256)');
      return;
    }
    setSearching(true);
    setError(null);
    setMpiResults(null);
    setCfResults(null);
    try {
      if (hashType === 'mpi') {
        const res = await apogeeApiService.findPacksByModlistHash(trimmed, hashSort);
        setMpiResults(res);
      } else {
        const res = await apogeeApiService.findPacksByCFModlistHash(trimmed, hashSort);
        setCfResults(res);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lookup failed');
    } finally {
      setSearching(false);
    }
  };

  const results = hashType === 'mpi' ? mpiResults?.items : cfResults?.items;
  const totalResults = hashType === 'mpi' ? mpiResults?.total : cfResults?.total;

  return (
    <>
      <div className="glass-card p-4 md:p-5 space-y-4">
        <SectionHeader
          title="Modlist Hash Lookup"
          icon={Hash}
          description="Find packs whose modlist matches a SHA-256 hash."
          actions={
            <button
              type="button"
              onClick={onClose}
              className="btn-icon"
              title="Close"
              aria-label="Close hash lookup"
            >
              <X className="w-4 h-4" />
            </button>
          }
        />

        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="segmented shrink-0" role="group" aria-label="Hash type">
            <button
              type="button"
              onClick={() => setHashType('mpi')}
              className="segmented-item"
              data-active={hashType === 'mpi' ? 'true' : 'false'}
              aria-pressed={hashType === 'mpi'}
            >
              MPI Modlist
            </button>
            <button
              type="button"
              onClick={() => setHashType('cf')}
              className="segmented-item"
              data-active={hashType === 'cf' ? 'true' : 'false'}
              aria-pressed={hashType === 'cf'}
            >
              CurseForge Modlist
            </button>
          </div>
          <input
            className="glass-input flex-1 min-w-0 font-mono"
            placeholder="Enter SHA-256 hash (64 hex chars)…"
            value={hashInput}
            onChange={(e) => setHashInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            aria-label="SHA-256 modlist hash"
          />
          <select
            className="glass-select md:w-52"
            value={hashSort}
            onChange={(e) => setHashSort(e.target.value)}
            title="Sort results"
            aria-label="Sort results"
          >
            {PACK_BROWSER_CATALOG_SORT_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
            {ACTIVITY_CATALOG_SORT_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
            <option value="mpi_id">MPI ID (low first)</option>
            <option value="-mpi_id">MPI ID (high first)</option>
          </select>
          <button
            onClick={handleSearch}
            disabled={searching || !hashInput.trim()}
            className="glass-button"
          >
            {searching ? <Loader className="w-4 h-4 animate-spin" /> : 'Search'}
          </button>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            <AlertCircle />
            <span>{error}</span>
          </div>
        )}
      </div>

      {results && (
        <div className="space-y-3">
          <p className="text-xs text-glass-400 tabular-nums">
            {totalResults === 0 ? 'No packs found for this hash.' : `Found ${totalResults} matching pack${totalResults === 1 ? '' : 's'}:`}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {results.map((row) => (
                <PackCard
                key={row.apogee_id}
                pack={{
                  apogee_id: row.apogee_id,
                  mpi_id: row.mpi_id,
                  cf_mod_id: row.cf_mod_id,
                  slug: row.slug,
                  name: row.name,
                  summary: row.summary,
                  thumbnail_url: row.thumbnail_url,
                  first_seen_at: row.first_seen_at,
                  first_seen_download_count: row.first_seen_download_count,
                  download_count: row.download_count,
                  popularity_rank: row.popularity_rank,
                  latest_release_date: null,
                  last_modified: null,
                  last_updated: row.last_updated ?? null,
                  snapshot_observed_at: row.snapshot_observed_at || '',
                  modrinth: row.modrinth,
                  curseforge: row.curseforge,
                  curseforge_featured: row.curseforge_featured,
                  apogee_activity: row.apogee_activity,
                }}
                enrichment={enrichmentFromPackRow(row)}
                onClick={() => navigate(packDetailPath(row))}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

// ── Main View ───────────────────────────────────────────────────────────────

export const ModpackBrowserView = () => {
  useDocumentTitle('Modpack Browser');
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // ── Apogee status ─────────────────────────────────────────────────────
  const [apogeeReady, setApogeeReady] = useState<boolean | null>(null);

  // ── Packs list state ──────────────────────────────────────────────────
  const [packs, setPacks] = useState<PackRow[]>([]);
  const [total, setTotal] = useState(0);
  /** Full-catalog `total` from `GET /v1/packs` (for stats while a search is active). */
  const [fullCatalogTotal, setFullCatalogTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchUnavailable, setSearchUnavailable] = useState(false);
  const [searchNotice, setSearchNotice] = useState<string | null>(null);
  const catalogAbortRef = useRef<AbortController | null>(null);
  // ── Pagination & sort ─────────────────────────────────────────────────
  const [limit, setLimit] = useState<number>(() => {
    const saved = localStorage.getItem('modpackBrowser-pageSize');
    const n = saved ? Number(saved) : 50;
    if (Number.isFinite(n) && n > 0 && n <= 200) return n;
    return 50;
  });
  const [offset, setOffset] = useState(0);
  const [catalogSort, setCatalogSort] = useState<string>(() => {
    const saved = localStorage.getItem('modpackBrowser-catalogSort');
    const allPresets = [
      ...PACK_BROWSER_CATALOG_SORT_PRESETS,
      ...ACTIVITY_CATALOG_SORT_PRESETS,
    ];
    if (saved && allPresets.some((p) => p.value === saved)) return saved;
    return '-download_count';
  });

  // ── Search ────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);

  // ── View mode ─────────────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    return (localStorage.getItem('modpackBrowser-viewMode') as ViewMode) || 'grid';
  });

  // ── Tab ───────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<BrowserTab>('all');

  // ── Insights data ─────────────────────────────────────────────────────
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [recentUpdatesCount, setRecentUpdatesCount] = useState<number | null>(null);
  const [topGrowth, setTopGrowth] = useState<DownloadGrowthRow | null>(null);
  const [topEmerging, setTopEmerging] = useState<EmergingRow | null>(null);

  // ── Insights tab data ─────────────────────────────────────────────────
  const [recentUpdates, setRecentUpdates] = useState<RecentUpdateRow[]>([]);
  const [recentUpdatesLoading, setRecentUpdatesLoading] = useState(false);
  const [sinceHours, setSinceHours] = useState(168);

  const [growthData, setGrowthData] = useState<DownloadGrowthRow[]>([]);
  const [growthLoading, setGrowthLoading] = useState(false);
  const [windowDays, setWindowDays] = useState(7);

  const [emergingData, setEmergingData] = useState<EmergingRow[]>([]);
  const [emergingLoading, setEmergingLoading] = useState(false);
  const [emergingDays, setEmergingDays] = useState(30);
  const [emergingMinDelta, setEmergingMinDelta] = useState(500);

  // ── Insight sort ──────────────────────────────────────────────────────
  const [recentSort, setRecentSort] = useState('-last_updated');
  const [growthSort, setGrowthSort] = useState('-delta_downloads');
  const [emergingSort, setEmergingSort] = useState('-delta_downloads');

  const growthApiSort = growthSort === GROWTH_SORT_RELATIVE_CLIENT ? '-delta_downloads' : growthSort;

  const displayedGrowthRows = useMemo(() => {
    if (growthSort !== GROWTH_SORT_RELATIVE_CLIENT) return growthData;
    const rows = [...growthData];
    rows.sort((a, b) => {
      const fa = growthFactorVsBaseline(a);
      const fb = growthFactorVsBaseline(b);
      if (fa != null && fb != null && fa !== fb) return fb - fa;
      if (fa != null && fb == null) return -1;
      if (fa == null && fb != null) return 1;
      return b.delta_downloads - a.delta_downloads;
    });
    return rows;
  }, [growthData, growthSort]);

  // ── Hash Lookup ───────────────────────────────────────────────────────
  const [showHashLookup, setShowHashLookup] = useState(false);

  // ── CurseForge featured & activity (card/table display) ───────────────
  const [activityCardWindow, setActivityCardWindow] = useState<ApogeeActivityWindow>('30d');
  const [featuredFilter, setFeaturedFilter] = useState<FeaturedFilter>('any');

  // ── Derived ───────────────────────────────────────────────────────────
  const isSearchActive = debouncedSearch.trim().length > 0;
  const pageSizeOptions = isSearchActive ? PAGE_SIZES_SEARCH : PAGE_SIZES_BROWSE;

  const totalPacksStatValue = useMemo(() => {
    if (fullCatalogTotal !== null && fullCatalogTotal > 0) return fullCatalogTotal;
    if (!isSearchActive && total > 0) return total;
    return null;
  }, [fullCatalogTotal, isSearchActive, total]);

  /** Row count for pagination in the All Packs tab (search hit total or degraded client matches). */
  const catalogListTotal = useMemo(() => {
    if (!isSearchActive) return total;
    if (searchUnavailable) {
      let list = packs;
      if (featuredFilter === 'featured') {
        list = list.filter((p) => p.curseforge_featured?.currently_featured === true);
      } else if (featuredFilter === 'not_featured') {
        list = list.filter((p) => {
          const f = p.curseforge_featured;
          return f == null || f.currently_featured !== true;
        });
      }
      return filterPacksClient(applyClientSort(list, catalogSort), debouncedSearch).length;
    }
    return total;
  }, [isSearchActive, searchUnavailable, packs, catalogSort, debouncedSearch, total, featuredFilter]);

  const currentPage = useMemo(() => Math.floor(offset / limit) + 1, [offset, limit]);
  const totalPages = useMemo(() => Math.ceil(catalogListTotal / limit), [catalogListTotal, limit]);

  const featuredOnPageCount = useMemo(
    () => packs.filter((p) => p.curseforge_featured?.currently_featured === true).length,
    [packs],
  );

  const displayedPacks = useMemo(() => {
    let list = packs;
    if (featuredFilter === 'featured') {
      list = list.filter((p) => p.curseforge_featured?.currently_featured === true);
    } else if (featuredFilter === 'not_featured') {
      list = list.filter((p) => {
        const f = p.curseforge_featured;
        return f == null || f.currently_featured !== true;
      });
    }
    if (!isSearchActive) return list;
    const sorted = applyClientSort(list, catalogSort);
    if (searchUnavailable) {
      const all = filterPacksClient(sorted, debouncedSearch);
      return all.slice(offset, offset + limit);
    }
    return sorted;
  }, [
    packs,
    debouncedSearch,
    isSearchActive,
    catalogSort,
    searchUnavailable,
    offset,
    limit,
    featuredFilter,
  ]);

  // ── Persist preferences ───────────────────────────────────────────────
  useEffect(() => {
    localStorage.setItem('modpackBrowser-viewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('modpackBrowser-pageSize', String(limit));
  }, [limit]);

  useEffect(() => {
    localStorage.setItem('modpackBrowser-catalogSort', catalogSort);
  }, [catalogSort]);

  // ── Check Apogee readiness ────────────────────────────────────────────
  useEffect(() => {
    apogeeApiService.readyCheck()
      .then(() => setApogeeReady(true))
      .catch(() => setApogeeReady(false));
  }, []);

  // ── Fetch packs / search ─────────────────────────────────────────────
  const fetchPacks = useCallback(async () => {
    if (activeTab !== 'all') return;
    catalogAbortRef.current?.abort();
    const ac = new AbortController();
    catalogAbortRef.current = ac;
    setLoading(true);
    setError(null);
    setSearchNotice(null);
    const q = debouncedSearch.trim();
    const ensureFullCatalogTotal = () => {
      void apogeeApiService
        .listPacks({ limit: 1, offset: 0, sort: '-download_count' }, ac.signal)
        .then((r) => setFullCatalogTotal((prev) => prev ?? (r.total || 0)))
        .catch(() => { /* non-critical */ });
    };
    try {
      if (q) {
        if (searchUnavailable) {
          const res = await apogeeApiService.listPacks(
            { limit: SEARCH_PACKS_MAX_LIMIT, offset: 0, sort: catalogSort },
            ac.signal,
          );
          setPacks(res.items || []);
          setSearchNotice('Server search is unavailable; showing matches from the first 100 packs only. Clear the search to browse the full catalog.');
          ensureFullCatalogTotal();
          return;
        }
        try {
          const res = await apogeeApiService.searchPacks({ q, limit, offset }, ac.signal);
          const hits = res.hits || [];
          const rows: PackRow[] = [];
          for (const h of hits) {
            if (ac.signal.aborted) return;
            let row = packRowFromSearchHit(h);
            if (!row) {
              const aid = apogeeIdFromSearchHit(h);
              if (aid != null) {
                try {
                  row = await apogeeApiService.getPackByApogeeId(aid);
                } catch {
                  /* skip hit */
                }
              }
            }
            if (row) rows.push(row);
          }
          setPacks(rows);
          setTotal(typeof res.total === 'number' ? res.total : rows.length);
          ensureFullCatalogTotal();
        } catch (err) {
          if (err instanceof ApogeeSearchDisabledError) {
            setSearchUnavailable(true);
            const res = await apogeeApiService.listPacks(
              { limit: SEARCH_PACKS_MAX_LIMIT, offset: 0, sort: catalogSort },
              ac.signal,
            );
            setPacks(res.items || []);
            setSearchNotice(`${err.message} Showing matches from the first 100 packs only. Clear the search to browse the full catalog.`);
            ensureFullCatalogTotal();
            return;
          }
          throw err;
        }
      } else {
        const res = await apogeeApiService.listPacks({ limit, offset, sort: catalogSort }, ac.signal);
        setPacks(res.items || []);
        const t = res.total || 0;
        setTotal(t);
        setFullCatalogTotal(t);
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load packs');
    } finally {
      if (catalogAbortRef.current === ac) {
        setLoading(false);
        catalogAbortRef.current = null;
      }
    }
  }, [limit, offset, catalogSort, activeTab, debouncedSearch, searchUnavailable]);

  useEffect(() => {
    fetchPacks();
  }, [fetchPacks]);

  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch]);

  useEffect(() => {
    const maxOff = Math.max(0, catalogListTotal - limit);
    if (offset > maxOff) setOffset(maxOff);
  }, [catalogListTotal, limit, offset]);

  // ── Fetch insights summary (once) ────────────────────────────────────
  useEffect(() => {
    const fetchInsights = async () => {
      setInsightsLoading(true);
      try {
        const [recentRes, growthRes, emergingRes] = await Promise.allSettled([
          apogeeApiService.getRecentUpdates({ limit: 50 }),
          apogeeApiService.getDownloadGrowth({ limit: 1 }),
          apogeeApiService.getEmerging({ limit: 1 }),
        ]);
        if (recentRes.status === 'fulfilled') setRecentUpdatesCount((recentRes.value.items || []).length);
        if (growthRes.status === 'fulfilled' && (growthRes.value.items || []).length > 0) setTopGrowth(growthRes.value.items[0]);
        if (emergingRes.status === 'fulfilled' && (emergingRes.value.items || []).length > 0) setTopEmerging(emergingRes.value.items[0]);
      } catch {
        // Non-critical — don't block UI
      } finally {
        setInsightsLoading(false);
      }
    };
    fetchInsights();
  }, []);

  // ── Fetch insight tab data ────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'recent') return;
    setRecentUpdatesLoading(true);
    apogeeApiService.getRecentUpdates({ since_hours: sinceHours, limit: 200, sort: recentSort })
      .then((res) => setRecentUpdates(res.items || []))
      .catch(() => {})
      .finally(() => setRecentUpdatesLoading(false));
  }, [activeTab, sinceHours, recentSort]);

  useEffect(() => {
    if (activeTab !== 'growth') return;
    setGrowthLoading(true);
    apogeeApiService.getDownloadGrowth({ window_days: windowDays, limit: 200, sort: growthApiSort })
      .then((res) => setGrowthData(res.items || []))
      .catch(() => {})
      .finally(() => setGrowthLoading(false));
  }, [activeTab, windowDays, growthApiSort]);

  useEffect(() => {
    if (activeTab !== 'emerging') return;
    setEmergingLoading(true);
    apogeeApiService.getEmerging({ first_seen_within_days: emergingDays, min_delta_downloads: emergingMinDelta, limit: 200, sort: emergingSort })
      .then((res) => setEmergingData(res.items || []))
      .catch(() => {})
      .finally(() => setEmergingLoading(false));
  }, [activeTab, emergingDays, emergingMinDelta, emergingSort]);

  // ── Pagination handlers ───────────────────────────────────────────────
  const goToPage = (page: number) => {
    setOffset((page - 1) * limit);
  };

  const handleCatalogSortChange = (sort: string) => {
    setCatalogSort(sort);
    setOffset(0);
  };

  const handlePageSizeChange = (newSize: number) => {
    setLimit(newSize);
    setOffset(0);
  };

  useEffect(() => {
    if (!debouncedSearch.trim()) {
      setSearchNotice(null);
      setSearchUnavailable(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    const max = isSearchActive ? SEARCH_PACKS_MAX_LIMIT : 200;
    if (limit > max) {
      setLimit(max);
      setOffset(0);
    }
  }, [isSearchActive, limit]);

  // ── Tab definitions ───────────────────────────────────────────────────
  const TABS: { id: BrowserTab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { id: 'all', label: 'All Packs', icon: Package },
    { id: 'recent', label: 'Recently Updated', icon: Clock },
    { id: 'growth', label: 'Download Growth', icon: TrendingUp },
    { id: 'emerging', label: 'Emerging', icon: Sparkles },
  ];

  // ── Table view ────────────────────────────────────────────────────────
  const renderTableView = () => (
    <div className="hidden md:block table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th className="th w-12"><span className="sr-only">Thumbnail</span></th>
            <th className="th">Name</th>
            <th className="th">Slug</th>
            <th className="th max-w-[200px]">Author</th>
            <th className="th text-right">Downloads</th>
            <th className="th text-right">Rank</th>
            <th className="th text-center w-16" title="CurseForge featured carousel">
              <Star className="w-3.5 h-3.5 inline text-amber-400" />
            </th>
            <th className="th text-right" title={`Activity score (${activityCardWindow})`}>
              <span className="inline-flex items-center justify-end gap-1 w-full">
                <Activity className="w-3 h-3 text-violet-400 shrink-0" />
                <span>Act. {activityCardWindow}</span>
              </span>
            </th>
            <th className="th text-right" title={`Activity rank (${activityCardWindow})`}>
              Act. #
            </th>
            <th className="th">First Release</th>
            <th className="th">First Seen</th>
            <th className="th">Last Updated</th>
          </tr>
        </thead>
        <tbody>
          {displayedPacks.map((pack) => (
            <tr
              key={pack.apogee_id}
              className="cursor-pointer"
              onClick={() => navigate(packDetailPath(pack))}
            >
              <td className="td">
                <div className="w-8 h-8 rounded-md overflow-hidden bg-dark-900/50 shrink-0">
                  {pack.thumbnail_url ? (
                    <img src={pack.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-4 h-4 text-glass-400" />
                    </div>
                  )}
                </div>
              </td>
              <td className="td text-white font-medium">{pack.name}</td>
              <td className="td text-xs font-mono text-glass-400">{pack.slug}</td>
              <td className="td text-xs text-glass-500 max-w-[200px] truncate" title={enrichmentAuthorDisplayLine(enrichmentFromPackRow(pack)) || undefined}>
                {enrichmentAuthorDisplayLine(enrichmentFromPackRow(pack)) || '—'}
              </td>
              <td className="td text-glass-300 text-right tabular-nums">{formatNumber(pack.download_count)}</td>
              <td className="td text-glass-300 text-right tabular-nums">
                {typeof pack.popularity_rank === 'number' ? `#${pack.popularity_rank}` : '—'}
              </td>
              <td className="td text-center">
                {pack.curseforge_featured?.currently_featured ? (
                  <span title="CurseForge featured carousel">
                    <Star className="w-4 h-4 text-amber-400 fill-amber-400/80 mx-auto" aria-label="CurseForge featured" />
                  </span>
                ) : (
                  <span className="text-glass-500">—</span>
                )}
              </td>
              <td className="td text-violet-300 text-right tabular-nums">
                {pack.apogee_activity?.windows?.[activityCardWindow]
                  ? pack.apogee_activity.windows[activityCardWindow]!.score.toFixed(1)
                  : '—'}
              </td>
              <td className="td text-glass-300 text-right tabular-nums">
                {typeof pack.apogee_activity?.windows?.[activityCardWindow]?.rank === 'number'
                  ? `#${pack.apogee_activity!.windows[activityCardWindow]!.rank}`
                  : '—'}
              </td>
              <td className="td text-xs text-glass-400 tabular-nums whitespace-nowrap">
                {pack.first_release_at ? formatDistanceToNow(new Date(pack.first_release_at), { addSuffix: true }) : '—'}
              </td>
              <td className="td text-xs text-glass-400 tabular-nums whitespace-nowrap">
                {formatDistanceToNow(new Date(pack.first_seen_at), { addSuffix: true })}
              </td>
              <td className="td text-xs text-glass-400 tabular-nums whitespace-nowrap">
                {pack.last_updated ? formatDistanceToNow(new Date(pack.last_updated), { addSuffix: true }) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // ── Insight tab: Recent Updates ───────────────────────────────────────
  const renderRecentUpdates = () => (
    <div className="space-y-4">
      <div className="glass-card p-3 flex flex-col md:flex-row md:items-center gap-2 flex-wrap">
        <label className="flex items-center gap-2 text-xs font-medium text-glass-400">
          <span className="whitespace-nowrap">Updated within</span>
          <select
            className="glass-select"
            value={sinceHours}
            onChange={(e) => setSinceHours(Number(e.target.value))}
          >
            {SINCE_HOURS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs font-medium text-glass-400">
          <span className="whitespace-nowrap">Sort</span>
          <select
            className="glass-select"
            value={recentSort}
            onChange={(e) => setRecentSort(e.target.value)}
          >
            {RECENT_SORT_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </label>
        {!recentUpdatesLoading && (
          <span className="text-xs text-glass-400 tabular-nums md:ml-auto">
            {recentUpdates.length.toLocaleString()} {recentUpdates.length === 1 ? 'pack' : 'packs'}
          </span>
        )}
      </div>
      {recentUpdatesLoading ? (
        <div className="space-y-3" aria-busy="true">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="glass-card p-3 flex items-center gap-4">
              <div className="flex-1 space-y-2">
                <SkeletonLoader className="h-4 w-48 max-w-full" />
                <SkeletonLoader className="h-3 w-32" />
              </div>
              <SkeletonLoader className="h-8 w-24" />
            </div>
          ))}
        </div>
      ) : recentUpdates.length === 0 ? (
        <EmptyState
          icon={Clock}
          title="No recently updated packs found"
          description="Try a wider time window."
        />
      ) : (
        <div className="space-y-3">
          {recentUpdates.map((row) => (
            <div
              key={row.apogee_id}
              className="glass-card glass-card-interactive p-3 flex items-center justify-between gap-4"
              onClick={() => navigate(packDetailPath(row))}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white truncate">{row.name}</p>
                <p className="text-xs font-mono text-glass-400 truncate">{row.slug}</p>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                {row.last_updated && (
                  <div className="text-right">
                    <p className="eyebrow">Updated</p>
                    <p className="text-xs text-glass-300 tabular-nums">{formatDistanceToNow(new Date(row.last_updated), { addSuffix: true })}</p>
                  </div>
                )}
                <div className="text-right">
                  <p className="eyebrow">Observed</p>
                  <p className="text-xs text-glass-300 tabular-nums">{formatDistanceToNow(new Date(row.observed_at), { addSuffix: true })}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── Insight tab: Download Growth ──────────────────────────────────────
  const renderDownloadGrowth = () => (
    <div className="space-y-4">
      <div className="glass-card p-3 flex flex-col md:flex-row md:items-center gap-2 flex-wrap">
        <label className="flex items-center gap-2 text-xs font-medium text-glass-400">
          <span className="whitespace-nowrap">Window</span>
          <select
            className="glass-select"
            value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value))}
          >
            {WINDOW_DAYS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs font-medium text-glass-400">
          <span className="whitespace-nowrap">Sort</span>
          <select
            className="glass-select"
            value={growthSort}
            onChange={(e) => setGrowthSort(e.target.value)}
          >
            {GROWTH_SORT_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </label>
        {!growthLoading && (
          <span className="text-xs text-glass-400 tabular-nums md:ml-auto">
            {displayedGrowthRows.length.toLocaleString()} {displayedGrowthRows.length === 1 ? 'pack' : 'packs'}
          </span>
        )}
      </div>
      {growthLoading ? (
        <div className="space-y-2" aria-busy="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="glass-card p-3 flex items-center gap-4">
              <SkeletonLoader className="h-5 w-8" />
              <div className="flex-1 space-y-2">
                <SkeletonLoader className="h-4 w-48 max-w-full" />
                <SkeletonLoader className="h-3 w-32" />
              </div>
              <SkeletonLoader className="h-8 w-20" />
            </div>
          ))}
        </div>
      ) : displayedGrowthRows.length === 0 ? (
        <EmptyState
          icon={TrendingUp}
          title="No growth data available"
          description="Growth rankings will appear once enough snapshots have been observed."
        />
      ) : (
        <div className="space-y-2">
          {displayedGrowthRows.map((row, idx) => (
            <div
              key={row.apogee_id}
              className="glass-card glass-card-interactive p-3 flex items-center justify-between gap-4"
              onClick={() => navigate(packDetailPath(row))}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-sm font-semibold text-glass-400 w-8 text-right shrink-0 tabular-nums">
                  {idx + 1}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{row.name}</p>
                  <p className="text-xs font-mono text-glass-400 truncate">{row.slug}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <div className="text-right">
                  <p className="eyebrow">Downloads</p>
                  <p className="text-sm font-medium text-glass-300 tabular-nums">{formatNumber(row.download_count_latest)}</p>
                </div>
                <div className="text-right min-w-[80px]">
                  <p className="eyebrow">Growth</p>
                  {growthSort === GROWTH_SORT_RELATIVE_CLIENT ? (
                    <>
                      <p className="text-sm font-semibold text-amber-400 tabular-nums">
                        {formatRelativeGrowthFactor(growthFactorVsBaseline(row))}
                      </p>
                      <p className="text-xs text-glass-500 tabular-nums">+{formatNumber(row.delta_downloads)}</p>
                    </>
                  ) : (
                    <p className="text-sm font-semibold text-green-400 tabular-nums">+{formatNumber(row.delta_downloads)}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── Insight tab: Emerging ─────────────────────────────────────────────
  const renderEmerging = () => (
    <div className="space-y-4">
      <div className="glass-card p-3 flex flex-col md:flex-row md:items-center gap-2 flex-wrap">
        <label className="flex items-center gap-2 text-xs font-medium text-glass-400">
          <span className="whitespace-nowrap">First seen within</span>
          <select
            className="glass-select"
            value={emergingDays}
            onChange={(e) => setEmergingDays(Number(e.target.value))}
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs font-medium text-glass-400">
          <span className="whitespace-nowrap">Min download growth</span>
          <select
            className="glass-select"
            value={emergingMinDelta}
            onChange={(e) => setEmergingMinDelta(Number(e.target.value))}
          >
            <option value={100}>100+</option>
            <option value={500}>500+</option>
            <option value={1000}>1,000+</option>
            <option value={5000}>5,000+</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs font-medium text-glass-400">
          <span className="whitespace-nowrap">Sort</span>
          <select
            className="glass-select"
            value={emergingSort}
            onChange={(e) => setEmergingSort(e.target.value)}
          >
            {EMERGING_SORT_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </label>
        {!emergingLoading && (
          <span className="text-xs text-glass-400 tabular-nums md:ml-auto">
            {emergingData.length.toLocaleString()} {emergingData.length === 1 ? 'pack' : 'packs'}
          </span>
        )}
      </div>
      {emergingLoading ? (
        <div className="space-y-2" aria-busy="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="glass-card p-3 flex items-center gap-4">
              <SkeletonLoader className="h-5 w-8" />
              <div className="flex-1 space-y-2">
                <SkeletonLoader className="h-4 w-48 max-w-full" />
                <SkeletonLoader className="h-3 w-32" />
              </div>
              <SkeletonLoader className="h-8 w-20" />
            </div>
          ))}
        </div>
      ) : emergingData.length === 0 ? (
        <EmptyState
          icon={Sparkles}
          title="No emerging packs found for these criteria"
          description="Try a longer first-seen window or a lower growth threshold."
        />
      ) : (
        <div className="space-y-2">
          {emergingData.map((row, idx) => (
            <div
              key={row.apogee_id}
              className="glass-card glass-card-interactive p-3 flex items-center justify-between gap-4"
              onClick={() => navigate(packDetailPath(row))}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-sm font-semibold text-glass-400 w-8 text-right shrink-0 tabular-nums">
                  {idx + 1}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{row.name}</p>
                  <p className="text-xs text-glass-400 tabular-nums">
                    First seen {formatDistanceToNow(new Date(row.first_seen_at), { addSuffix: true })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <div className="text-right">
                  <p className="eyebrow">Downloads</p>
                  <p className="text-sm font-medium text-glass-300 tabular-nums">{formatNumber(row.download_count_latest)}</p>
                </div>
                <div className="text-right min-w-[80px]">
                  <p className="eyebrow">Growth</p>
                  <p className="text-sm font-semibold text-green-400 tabular-nums">+{formatNumber(row.delta_downloads)}</p>
                </div>
                {row.growth_factor_vs_baseline != null && (
                  <div className="text-right min-w-[60px]">
                    <p className="eyebrow">Factor</p>
                    <p className="text-sm font-semibold text-amber-400 tabular-nums">{Number(row.growth_factor_vs_baseline).toFixed(1)}×</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── Pagination ────────────────────────────────────────────────────────
  const renderPagination = () => {
    if (activeTab !== 'all' || catalogListTotal === 0) return null;

    return (
      <div className="glass-card p-3 flex flex-col sm:flex-row items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-xs text-glass-400 tabular-nums">
          <span>Show</span>
          <select
            className="glass-select"
            value={limit}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            aria-label="Page size"
          >
            {pageSizeOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <span>of {catalogListTotal.toLocaleString()} {isSearchActive ? 'matches' : 'packs'}</span>
        </label>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1}
            className="btn-icon border-glass-200/10"
            title="Previous page"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-glass-400 tabular-nums px-1 whitespace-nowrap">
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className="btn-icon border-glass-200/10"
            title="Next page"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        eyebrow="Planning"
        title="Modpack Browser"
        description="Browse, search, and analyze modpacks from the Modpack Index via Apogee."
        icon={Package}
        meta={
          apogeeReady !== null ? (
            <span className={cn('badge badge-dot', apogeeReady ? 'badge-success' : 'badge-danger')}>
              {apogeeReady ? 'Apogee Online' : 'Apogee Offline'}
            </span>
          ) : undefined
        }
        actions={
          <>
            <button
              onClick={() => setShowHashLookup((prev) => !prev)}
              className={cn('glass-button', showHashLookup && 'border-accent-500/40')}
              title="Modlist Hash Lookup"
              aria-label="Modlist Hash Lookup"
              aria-expanded={showHashLookup}
            >
              <Hash className="w-4 h-4" />
              <span className={cn(isMobile && 'hidden')}>Hash Lookup</span>
            </button>
            <button
              onClick={fetchPacks}
              className="btn-icon border-glass-200/10"
              title="Refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
            </button>
          </>
        }
      />

      {/* Insights Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatTile
          label="Total Packs"
          value={totalPacksStatValue !== null ? totalPacksStatValue.toLocaleString() : '—'}
          icon={Database}
          tone="accent"
          loading={loading && totalPacksStatValue === null && !isSearchActive}
        />
        <StatTile
          label="Recently Updated"
          value={recentUpdatesCount !== null ? String(recentUpdatesCount) : '—'}
          hint="last 7 days"
          icon={Clock}
          tone="info"
          loading={insightsLoading}
          active={activeTab === 'recent'}
          onClick={() => setActiveTab('recent')}
        />
        <StatTile
          label="Top Growth"
          value={topGrowth ? `+${formatNumber(topGrowth.delta_downloads)}` : '—'}
          hint={topGrowth?.name}
          icon={TrendingUp}
          tone="success"
          loading={insightsLoading}
          active={activeTab === 'growth'}
          onClick={() => setActiveTab('growth')}
        />
        <StatTile
          label="Top Emerging"
          value={topEmerging ? `+${formatNumber(topEmerging.delta_downloads)}` : '—'}
          hint={topEmerging?.name}
          icon={Sparkles}
          tone="amber"
          loading={insightsLoading}
          active={activeTab === 'emerging'}
          onClick={() => setActiveTab('emerging')}
        />
        <StatTile
          label="CF Featured"
          value={loading && packs.length === 0 ? '—' : String(featuredOnPageCount)}
          hint="on this page"
          icon={Star}
          tone="warning"
          loading={loading && packs.length === 0}
          active={activeTab === 'all' && featuredFilter === 'featured'}
          onClick={() => { setActiveTab('all'); setFeaturedFilter('featured'); setOffset(0); }}
        />
      </div>

      {/* Hash Lookup */}
      {showHashLookup && (
        <HashLookupPanel onClose={() => setShowHashLookup(false)} />
      )}

      {/* Tab Bar */}
      <div className="tabs" role="tablist">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => { setActiveTab(tab.id); if (tab.id === 'all') setOffset(0); }}
              className="tab"
              data-active={activeTab === tab.id ? 'true' : 'false'}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* All Packs Tab — Search + Sort + Grid/Table */}
      {activeTab === 'all' && (
        <>
          {/* Toolbar */}
          <div className="glass-card p-3 flex flex-col md:flex-row md:items-start gap-2 flex-wrap">
            <ApogeePackCatalogSearchBar
              className="flex-1 min-w-0 md:min-w-[16rem]"
              value={searchQuery}
              onChange={setSearchQuery}
              loading={activeTab === 'all' && loading && debouncedSearch.trim().length > 0}
              placeholder="Search the catalog — names, slugs, summaries, Modrinth/CurseForge, links, MC versions…"
            />
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-wrap">
              <select
                className="glass-select sm:w-52"
                value={catalogSort}
                onChange={(e) => handleCatalogSortChange(e.target.value)}
                title={isSearchActive ? 'Sort this page of results' : 'Sort catalog'}
                aria-label="Sort"
              >
                {PACK_BROWSER_CATALOG_SORT_PRESETS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
                {ACTIVITY_CATALOG_SORT_PRESETS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <div className="segmented self-start" role="group" aria-label="View mode">
                <button
                  type="button"
                  onClick={() => setViewMode('grid')}
                  className="segmented-item"
                  data-active={viewMode === 'grid' ? 'true' : 'false'}
                  aria-pressed={viewMode === 'grid'}
                  title="Grid View"
                  aria-label="Grid View"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className="segmented-item"
                  data-active={viewMode === 'table' ? 'true' : 'false'}
                  aria-pressed={viewMode === 'table'}
                  title="Table View"
                  aria-label="Table View"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
              <label className="flex items-center gap-2 text-xs font-medium text-glass-400">
                <span className="whitespace-nowrap">Cards activity</span>
                <select
                  className="glass-select"
                  value={activityCardWindow}
                  onChange={(e) => setActivityCardWindow(e.target.value as ApogeeActivityWindow)}
                  title="Window shown on pack cards and table activity columns"
                >
                  {APOGEE_ACTIVITY_WINDOWS.map((w) => (
                    <option key={w} value={w}>{w}</option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 text-xs font-medium text-glass-400">
                <span className="whitespace-nowrap">CF featured</span>
                <select
                  className="glass-select"
                  value={featuredFilter}
                  onChange={(e) => setFeaturedFilter(e.target.value as FeaturedFilter)}
                >
                  <option value="any">Any</option>
                  <option value="featured">On carousel</option>
                  <option value="not_featured">Not on carousel</option>
                </select>
              </label>
            </div>
          </div>

          {searchNotice && (
            <div className="alert alert-warning" role="status">
              <AlertCircle />
              <span>{searchNotice}</span>
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" aria-busy="true">
              {Array.from({ length: limit > 12 ? 12 : limit }).map((_, i) => (
                <div key={i} className="glass-card overflow-hidden">
                  <SkeletonLoader className="h-36 w-full rounded-none" />
                  <div className="p-3 space-y-2">
                    <SkeletonLoader className="h-4 w-3/4" />
                    <SkeletonLoader className="h-3 w-1/2" />
                    <SkeletonLoader className="h-3 w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <EmptyState
              tone="danger"
              icon={AlertCircle}
              title="Failed to load packs"
              description={error}
              action={<button onClick={fetchPacks} className="glass-button">Retry</button>}
            />
          ) : displayedPacks.length === 0 ? (
            <EmptyState
              icon={debouncedSearch ? Search : Package}
              title={debouncedSearch ? 'No packs match your search' : 'No packs found'}
              description={debouncedSearch ? 'Try a different name, slug or platform.' : 'Packs from the Apogee catalog will appear here.'}
              action={debouncedSearch ? <button onClick={() => setSearchQuery('')} className="glass-button-secondary">Clear search</button> : undefined}
            />
          ) : (
            <>
              {viewMode === 'grid' || isMobile ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {displayedPacks.map((pack) => (
                    <PackCard
                      key={pack.apogee_id}
                      pack={pack}
                      enrichment={enrichmentFromPackRow(pack)}
                      activityWindow={activityCardWindow}
                      onClick={() => navigate(packDetailPath(pack))}
                    />
                  ))}
                </div>
              ) : (
                renderTableView()
              )}
              {/* Mobile card view fallback for table mode */}
              {viewMode === 'table' && isMobile && (
                <div className="space-y-3">
                  {displayedPacks.map((pack) => (
                    <PackCard
                      key={pack.apogee_id}
                      pack={pack}
                      enrichment={enrichmentFromPackRow(pack)}
                      activityWindow={activityCardWindow}
                      onClick={() => navigate(packDetailPath(pack))}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {renderPagination()}
        </>
      )}

      {/* Insights Tabs */}
      {activeTab === 'recent' && renderRecentUpdates()}
      {activeTab === 'growth' && renderDownloadGrowth()}
      {activeTab === 'emerging' && renderEmerging()}
    </div>
  );
};
